import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { HelpCircle, Loader2, ArrowLeft, MessageCircleQuestion } from "lucide-react";
import { askQuestionStream } from "../lib/api";
import { parseSections } from "../lib/parseSections";
import { addHistory } from "../lib/history";
import { AnswerDisplay } from "../components/AnswerDisplay";
import { EmergencyBanner } from "../components/EmergencyBanner";
import { QuestionBox } from "../components/QuestionBox";
import { Disclaimer } from "../components/Disclaimer";
import { useT } from "../lib/i18n";

export default function AnswerPage() {
  const t = useT();
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const openReview = params.get("review") === "1";

  const [displayQuestion, setDisplayQuestion] = useState(q);
  const [meta, setMeta] = useState(null);
  const [streamText, setStreamText] = useState("");
  const [done, setDone] = useState(false);
  const [sources, setSources] = useState([]);
  const [answerId, setAnswerId] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState(null);

  const ask = useCallback((text, historyArg, { keepDisplay, signal } = {}) => {
    setMeta(null);
    setStreamText("");
    setDone(false);
    setSources([]);
    setAnswerId(null);
    setError(null);
    setPending(null);
    if (!keepDisplay) setDisplayQuestion(text);
    window.scrollTo(0, 0);

    let full = "";
    let categoryLabel = "";
    askQuestionStream(text, historyArg, {
      signal,
      onMeta: (evt) => {
        setMeta(evt);
        categoryLabel = evt.category_label || "";
        if (evt.status === "followup") {
          setPending({ original: text, followupQuestion: evt.followup_question });
        }
      },
      onDelta: (c) => {
        full += c;
        setStreamText((prev) => prev + c);
      },
      onDone: (evt) => {
        setDone(true);
        setSources(evt.sources || []);
        setAnswerId(evt.answer_id || null);
        const simple = parseSections(full).simple_answer || "";
        if (simple) addHistory(text, categoryLabel);
        setHistory([
          ...historyArg,
          { role: "user", content: text },
          { role: "assistant", content: simple },
        ]);
      },
      onError: (m) => setError(m),
    });
  }, []);

  useEffect(() => {
    if (q) {
      setHistory([]);
      const controller = new AbortController();
      ask(q, [], { signal: controller.signal });
      return () => controller.abort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const answerFollowupPrompt = (ans) => {
    ask(ans, [
      { role: "user", content: pending.original },
      { role: "assistant", content: pending.followupQuestion },
    ], { keepDisplay: true });
  };

  const askAnother = (text) => ask(text, history);

  const isFollowup = meta && meta.status === "followup";
  const showAnswer = meta && !isFollowup;
  const result = {
    question: displayQuestion,
    category: meta?.category,
    category_label: meta?.category_label,
    emergency: meta?.emergency,
    answer: parseSections(streamText),
    sources,
    answer_id: answerId,
    status: meta?.status,
    answer_lang: meta?.answer_lang,
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link
        to="/"
        data-testid="back-home-link"
        data-no-print
        className="inline-flex items-center gap-2 text-base font-semibold text-slate-600 hover:text-teal-700 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> {t("back_home")}
      </Link>

      <div className="mb-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">
          {t("your_question")}
        </h2>
        <div
          className="bg-white border-2 border-slate-200 rounded-2xl p-5 flex items-start gap-3"
          data-testid="your-question"
        >
          <HelpCircle className="w-6 h-6 text-teal-600 shrink-0 mt-0.5" />
          <p className="text-xl text-slate-900 font-medium leading-relaxed">
            {displayQuestion}
          </p>
        </div>
      </div>

      {!meta && !error && (
        <div
          className="bg-white border-2 border-slate-200 rounded-2xl p-10 text-center"
          data-testid="answer-loading"
        >
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin mx-auto" />
          <p className="mt-4 text-lg text-slate-600 font-medium">
            {t("answer_loading")}
          </p>
        </div>
      )}

      {error && (
        <div
          className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 text-red-700 text-lg"
          data-testid="answer-error"
        >
          {error}
        </div>
      )}

      {isFollowup && (
        <div className="space-y-5 animate-fade-up">
          <EmergencyBanner emergency={meta.emergency} />
          <div
            className="bg-teal-50 border-2 border-teal-200 rounded-2xl p-6"
            data-testid="followup-prompt"
          >
            <h3 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2.5 mb-2">
              <MessageCircleQuestion className="w-6 h-6 text-teal-600" /> {t("followup_heading")}
            </h3>
            <p className="text-lg text-slate-800 leading-relaxed mb-4">
              {meta.followup_question}
            </p>
            <QuestionBox
              testIdPrefix="followup-answer"
              placeholder={t("followup_answer_placeholder")}
              buttonLabel={t("followup_continue")}
              onSubmit={answerFollowupPrompt}
            />
          </div>
          <Disclaimer compact />
        </div>
      )}

      {showAnswer && (
        <>
          <AnswerDisplay result={result} onFollowUp={askAnother} done={done} reviewLookup={true} autoOpenReview={openReview} />
          <div className="mt-6">
            <Disclaimer compact />
          </div>
        </>
      )}
    </div>
  );
}
